import Bugsnag from "@bugsnag/js";
import { lazy, Suspense, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import styled from "styled-components";
import { BooleanParam, StringParam, useQueryParams } from "use-query-params";

import axiosInstance from "../../axiosInstance";
import AiChat from "../../components/common/AiChat";
import SessionTranscriptionModal from "../../components/AIHome/SessionTranscriptionModal";
import { Case, Switch } from "../../components/common/ConditionalRendering";
import FlexBox from "../../components/common/FlexBox";
import Loader from "../../components/common/Loader";
import AttendanceModal from "../../components/Dashboard/AttendanceModal";
import { trackEvent } from "../../helperFunctions";
import useMobileView from "../../hooks/useMobileView";
import useNspVerification, {
  NspStatusViews,
} from "../../hooks/useNspVerification";
import { removeAllToasts } from "../../Store/Actions";
import URL from "../../urls";
import { isEmpty } from "../../utils/helper";
import { CF_PROVIDER_ROLE } from "../../variable";
import BottomNav from "./BottomNav";
import ChatModal from "./ChatModal";
import MobileHeader from "./MobileHeader";

const NotesModal = lazy(
  () => import("../../components/Clients/ClientProfile/AllNotes/NotesModal")
);
const PrivateNotesModal = lazy(
  () =>
    import("../../components/Clients/ClientProfile/AllNotes/PrivateNotesModal")
);
const GeneralisedNSPModal = lazy(
  () =>
    import("../../components/Clients/ClientProfile/Sessions/GeneralizedNspModal")
);
const NspConfirmationModal = lazy(
  () =>
    import("../../components/Clients/ClientProfile/Sessions/NspConfirmationModal")
);
const ClientDetails = lazy(
  () => import("../../components/Clients/ProfileHeader/ClientDetails")
);
const EscalateRiskModal = lazy(
  () => import("../../components/Clients/ProfileHeader/EscalateRiskModal")
);

const { CONFIRMATION_MODAL, NSP_MODAL } = NspStatusViews;

const Container = styled(FlexBox)`
  height: 100vh;
  width: 100vw;
  justify-content: space-between;
  flex-direction: column;
`;

const Content = styled(FlexBox)`
  height: ${({ showHeader, showBottomNav }) =>
    showHeader && showBottomNav
      ? "calc(100vh - 9rem)"
      : showHeader || showBottomNav
        ? "calc(100vh - 4.5rem)"
        : "100vh"};
  width: 100vw;
  flex-direction: column;
`;

const ButtonsContainer = styled(FlexBox)`
  position: absolute;
  bottom: 5rem;
  right: 1.25rem;
  align-items: flex-end;
  gap: 0.75rem;
  z-index: 20;
`;

const MobileLayout = ({ children }) => {
  const user = useSelector(state => state.auth.user);
  const showHeader = useSelector(state => state.mobileNav.showTopNav);
  const showBottomNav = useSelector(state => state.mobileNav.showBottomNav);
  const dispatch = useDispatch();
  const toasts = useSelector(state => state.toast.toasts);
  const providerProfile = useSelector(state => state.provider.providerProfile);
  const isAIEnabled = useSelector(state => state.featureFlags.ai_enabled);
  const isAIChatFeatureEnabled = useSelector(
    state => state.featureFlags.ai_chat_feature
  );
  const aiChatFeatureVersion = useSelector(
    state => state.featureFlags.ai_chat_feature_version
  );
  const isTwaMasterAIConsentEnabled = useSelector(
    state => state.featureFlags.twa_master_ai_consent
  );

  const isCoupleTherapist =
    providerProfile?.is_couples_therapist ||
    providerProfile?.roles?.includes("couple-therapist");

  const isMobile = useMobileView();

  const [queryParams, setQueryParams] = useQueryParams({
    showClientDetailsModal: BooleanParam,
    showChatModal: BooleanParam,
    showNotesModal: BooleanParam,
    showPrivateNotesModal: BooleanParam,
    showAddPrescriptionModal: BooleanParam,
    showAddDiagnosisModal: BooleanParam,
    showNspModal: BooleanParam,
    showUpdateProviderModal: BooleanParam,
    showAddConcernsModal: BooleanParam,
    showAllToolsModal: BooleanParam,
    showAddEhrModal: BooleanParam,
    showMgafModal: BooleanParam,
    showCgiModal: BooleanParam,
    showLinkedAccountsModal: BooleanParam,
    showAttendanceModal: BooleanParam,
    showAIConsentModal: BooleanParam,
    showEscalateRiskModal: BooleanParam,
    aiConsentSource: StringParam,
  });

  const {
    showClientDetailsModal,
    showChatModal,
    showNotesModal,
    showPrivateNotesModal,
    showAttendanceModal,
    showAIConsentModal,
    showEscalateRiskModal,
  } = queryParams;

  const { addToast } = useToasts();
  const [isTranscriptionEnabled, setIsTranscriptionEnabled] = useState(false);

  const { activeNspModal, setActiveNspModal } = useNspVerification({
    isCoupleTherapist,
  });

  useEffect(() => {
    setIsTranscriptionEnabled(isAIEnabled);
  }, [isAIEnabled]);

  useEffect(() => {
    dispatch(removeAllToasts());

    toasts.map(toast =>
      addToast(toast.msg, {
        appearance: toast.appearance || "success",
        autoDismiss: toast.autoDismiss || true,
        autoDismissTimeout: toast.autoDismissTimeout
          ? toast.autoDismissTimeout
          : isMobile
            ? 2000
            : 5000,
        showUndoBtn: toast.showUndoBtn || false,
        onUndoClick: toast.onUndoClick,
        showBottomToastOnMobile: toast.showBottomToastOnMobile || false,
      })
    );
  }, [isMobile, toasts, addToast, dispatch]);

  useEffect(() => {
    if (!user) return;
    const aiPermissionModalShown = localStorage.getItem(
      "aiPermissionModalShown"
    );
    if (aiPermissionModalShown === "true") return;

    const fetchAIConsentStatus = async () => {
      try {
        if (!user || isEmpty(providerProfile)) return;

        const isCfProvider = providerProfile?.roles?.some(role =>
          CF_PROVIDER_ROLE.includes(role)
        );

        if (isCfProvider) return;

        const res = await axiosInstance.get(URL.aiPermission, {
          params: { user_ids: user.id },
        });
        if (!res?.data?.data?.[user.id]) {
          setQueryParams(
            { showAIConsentModal: 1, aiConsentSource: "dashboard" },
            "replaceIn"
          );
          trackEvent({
            event: "twa_ai_permission_modal_shown",
            payload: { source: "dashboard" },
          });
        }
        localStorage.setItem("aiPermissionModalShown", "true");
      } catch (err) {
        Bugsnag.notify(err);
      }
    };
    fetchAIConsentStatus();
  }, [user, providerProfile]);

  const location = useLocation();

  const isAnalyticsPage = location?.pathname?.includes("analytics");
  const isDashboardPage = location.pathname?.includes("dashboard");
  const isClientsPage =
    location.pathname?.includes("clients") && !isAnalyticsPage;

  const showAiChatButton =
    isAIChatFeatureEnabled && aiChatFeatureVersion === "v3";
  const shouldShowAIConsentModal =
    isTwaMasterAIConsentEnabled && !!showAIConsentModal;
  const closeTranscriptionModal = () =>
    setQueryParams({ showAIConsentModal: null }, "replaceIn");

  return (
    <Container>
      <Suspense fallback={<Loader />}>
        {showChatModal && <ChatModal />}
        {showClientDetailsModal && <ClientDetails />}
        {showNotesModal && <NotesModal />}
        {showPrivateNotesModal && <PrivateNotesModal />}
        {showHeader && <MobileHeader />}
        {showAttendanceModal && <AttendanceModal />}
        {showEscalateRiskModal && <EscalateRiskModal />}
        <SessionTranscriptionModal
          isOpen={shouldShowAIConsentModal}
          onClose={closeTranscriptionModal}
          isEnabled={isTranscriptionEnabled}
          setIsEnabled={setIsTranscriptionEnabled}
        />
        <Switch>
          <Case condition={activeNspModal === CONFIRMATION_MODAL}>
            <NspConfirmationModal setActiveNspModal={setActiveNspModal} />
          </Case>
          <Case condition={activeNspModal === NSP_MODAL}>
            <GeneralisedNSPModal />
          </Case>
        </Switch>
      </Suspense>
      <Content showHeader={showHeader} showBottomNav={showBottomNav}>
        {children}
      </Content>
      <ButtonsContainer>{showAiChatButton && <AiChat />}</ButtonsContainer>
      {showBottomNav && <BottomNav />}
    </Container>
  );
};

export default MobileLayout;
